<script setup>

import {onMounted, ref} from 'vue';
import fileService from '@/services/FileService';
import CustomDropzone from '@/components/forms/CustomDropzone.vue';
import {useDataTable} from "@/utils/datatableHelper";
import {useI18n} from 'vue-i18n';
import {getApiBaseUrl} from '@/helper';

const {t} = useI18n()
const props = defineProps({
  fileable: String,
  fileableId: {
    type: Number,
    default: null,
  },
  files: {
    type: Array,
    default: [],
  },
  dropzoneDisabled: Boolean,
  fetchFiles: {
    type: Boolean,
    default: true,
  },
  singleFile: {
    type: Boolean,
    default: false,
  },
  acceptedFiles: {
    type: String,
    default: '.jpg, .png, .pdf, .docx, .xlsx',
  },
  showFilePreview: {
    type: Boolean,
    default: false,
  },
});

const isLoading = ref(false);
const files = ref([]);

const datatable = useDataTable(fetchFiles, 'name', 20, true);
const columns = ref([
  {
    label: t('fileListComponent.name'),
    field: 'name',
    filterOptions: {
      enabled: false,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: t('fileListComponent.action'),
    field: 'action',
    sortable: false,
    html: true
  },
]);

function fetchFiles() {
    isLoading.value = true;
    if (props.fetchFiles) {
      fileService.getFilesListByEntity(props.fileable, props.fileableId)
          .then((response) => {
            console.log(response);
            datatable.totalRecords.value = response.meta.total;
            files.value = response.data;
            isLoading.value = false;
          })
          .catch((error) => {
            console.log(error);
          });
    } else {
      console.log(files.value);
      files.value = props.files;
      isLoading.value = false;
    }
}

function handleDeleteFile(file) {
  fileService.deleteFile(file.id)
      .then(() => {
        getFiles();
      })
      .catch((error) => {
        console.log(error);
      });
}


function uploadMultipleSuccess(files) {
  if (props.showFilePreview) {
    getFiles();
  }
}

function onUploadMultipleError(error) {
  console.log(error);
}

function onUploadSuccess(file) {
  if (props.showFilePreview) {
    getFiles();
  }
}

function onUploadError(error) {
  console.log(error);
}

function onSend() {
  console.log('onSend called')
}

async function downloadFile(file) {
  fileService.downloadFileWithoutBase64(file);
}

function closeModal() {
  emitter.emit('close-modal');
}

onMounted(() => {
});
</script>

<template>
  <div v-if="!dropzoneDisabled">
    <CustomDropzone :dropzone-options-input="{
        url: getApiBaseUrl() + '/api/files/upload',
        acceptedFiles: props.acceptedFiles.replaceAll(' ', ''),
        maxFiles: props.singleFile ? 1 : 20,
        uploadMultiple: !props.singleFile,
        paramName: props.singleFile ? 'file' : 'files',
        params: {
          'fileable': props.fileable,
          'fileable_id': props.fileableId
        },
        init() {
          this.on('sendingmultiple', function (file, xhr, formData) {
            formData.append('fileable_type', props.fileable);
            formData.append('fileable_id', props.fileableId);
          });
          this.on('sending', function (file, xhr, formData) {
            formData.append('fileable_type', props.fileable);
            formData.append('fileable_id', props.fileableId);
          });
        }
      }" :previews-disabled="props.showFilePreview" :upload-multiple-success="uploadMultipleSuccess" :upload-multiple-error="onUploadMultipleError" :upload-success="onUploadSuccess" :upload-error="onUploadError" :on-send="onSend"/>
  </div>
  <div v-if="props.showFilePreview" class="file-preview-container">
    <div class="file-preview" v-for="file in files" :key="file.id">
      <span class="file-icon" :class="'file-icon__' + file.extension"> {{ file.extension }} </span>
      <span class="overflow-hidden overflow-ellipsis whitespace-nowrap flex-1"> {{ file.name }} </span>
      <div class="dropdown dropdown-start">
        <div tabindex="0" role="button" class="ml-1"><span class="mdi mdi-chevron-down text-xl"></span></div>
        <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 shadow">
          <li @click="downloadFile(file)" class="hover:bg-base-300 py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponent.download') }}
          </li>
          <li @click="handleDeleteFile(file)" class="hover:bg-error py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponent.delete') }}
          </li>
        </ul>
      </div>
    </div>
    <div v-if="files.length === 0" class="flex justify-center">
      {{ t('fileListComponent.no_files_uploaded') }}
    </div>
  </div>
  <div v-if="!props.showFilePreview">
    <vue-good-table
        :columns="columns"
        :rows="files"
        mode="remote"
        @page-change="datatable.onPageChange"
        @sort-change="datatable.onSortChange"
        @column-filter="datatable.onColumnFilter"
        @per-page-change="datatable.onPerPageChange"
        :totalRows="datatable.totalRecords.value"
        :pagination-options="{
          enabled: true,
          rowsPerPageLabel: t('investmentIndexTableComponent.entriesPerPage'),
          nextLabel: t('investmentIndexTableComponent.next'),
          prevLabel: t('investmentIndexTableComponent.previous'),
          ofLabel: 'z',
        }">
            <template #table-row="props">
                <span v-if="props.column.field === 'action'">
                    <span style="font-weight: bold; float: right">
                      <button class="btn btn-link" @click.stop.prevent="downloadFile(props.row)"><i class="mdi mdi-download mdi-18px"></i></button>
                      <router-link @click="closeModal" :to="{ name: 'FileDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
                    </span>
                </span>
            </template>
            <template v-slot:emptystate v-if="!isLoading">
                <div class="text-center">
                  {{ t('fileListComponent.no_records_found') }}
                </div>
            </template>
            <template v-slot:emptystate v-if="isLoading">
                <div class="text-center">
                  {{ t('fileListComponent.data_loading') }}
                </div>
            </template>
    </vue-good-table>
  </div>

</template>

<style scoped>

.file-preview-container {
  display: flex;
  flex-wrap: wrap;
}

.file-preview {
  max-width: 300px;
  min-width: 300px;
  max-height: 60px;
  border: 1px solid #e2e8f0;
  padding: 10px;
  display: flex;
  align-items: center;
  margin: 0 10px 10px 0;
  border-radius: 5px;
}

.file-icon {
  vertical-align: middle;
  background: rgb(187, 230, 103);
  background: linear-gradient(
      140deg,
      rgb(187, 230, 103) 0%,
      rgb(187, 230, 103) 100%
  );
  border: none;
  display: inline-block;
  width: 36px;
  height: 40px;
  line-height: 48px;
  text-align: center;
  border-radius: 5px;
  color: white;
  font-size: 0.9em;
  font-weight: 700;
  margin-right: 10px;
  min-width: 40px;
  min-height: 40px;
}

.file-icon__pdf {
  background: rgb(247, 85, 85);
  background: linear-gradient(
      140deg,
      rgb(247, 85, 85) 0%,
      rgb(213, 45, 45) 100%
  );
}

.file-icon__xlsx,
.file-icon__xls {
  background: rgb(45, 91, 58);
  background: linear-gradient(
      140deg,
      rgb(99, 193, 135) 0%,
      rgb(45, 91, 58) 100%
  );
}

.file-icon__docx,
.file-icon__doc {
  background: rgb(30, 62, 140);
  background: linear-gradient(
      140deg,
      rgb(93, 163, 232) 0%,
      rgb(30, 62, 140) 100%
  );
}

.file-icon__ppt,
.file-icon__pptx {
  background: rgb(196, 90, 59);
  background: linear-gradient(
      140deg,
      rgb(240, 149, 115) 0%,
      rgb(196, 90, 59) 100%
  );
}

</style>
